import { FormattedMessage, useIntl } from "react-intl";
import { Fragment, ReactNode } from "react";
import styles from "./ExploreBorrowingFields.module.scss";
import useAssetAvailability, {
  BorrowStatus,
} from "../../actions/explore/activate/useAssetAvailability";
import clsx from "clsx";
import ReturnButton from "../explore/ReturnButton";
import { getExploreDuration } from "../explore/exploreDuration.ts";
import ExploreDurationMessage from "../explore/ExploreDurationMessage.tsx";
import { getDaysRemaining } from "../../my-courses/index/timeHelper.tsx";

interface FieldType {
  label: string;
  value: ReactNode;
  enabled: boolean;
  className?: string;
}

export default function ExploreBorrowingFields({
  explore,
}: {
  explore: VST.Bridge.Asset;
}) {
  const intl = useIntl();
  const { getMatchingCredits, getBorrowStatus, creditsAvailable } =
    useAssetAvailability(explore);
  const { matchingAssetCredit, matchingUserCredit } = getMatchingCredits();
  const borrowStatus = getBorrowStatus();

  const getAvailability = () => {
    if (!explore.distributable) {
      return {
        value: <FormattedMessage defaultMessage={"Out of distribution"} />,
        className: styles.red,
      };
    }
    if (borrowStatus === BorrowStatus.Borrowable) {
      return {
        value: (
          <FormattedMessage
            defaultMessage="{available} of {total, plural, =1 {1 copy} other {# copies}} left"
            values={{
              available: matchingAssetCredit?.copies?.available,
              total: matchingAssetCredit?.copies?.total,
            }}
          />
        ),
      };
    }

    if (
      borrowStatus === BorrowStatus.NoCopies ||
      borrowStatus === BorrowStatus.NoCreditsOrCopies
    ) {
      return {
        value: (
          <FormattedMessage
            defaultMessage="Unavailable. {available} of {total, plural, =1 {1 copy} other {# copies}} left"
            values={{
              available: matchingAssetCredit?.copies?.available,
              total: matchingAssetCredit?.copies?.total,
            }}
          />
        ),
        className: styles.red,
      };
    }
    return {
      value: <FormattedMessage defaultMessage="Unavailable" />,
      className: styles.red,
    };
  };

  const getLoanPeriodValue = () => {
    if (explore.active) {
      const { hasDuration, days } = getExploreDuration(explore);

      if (hasDuration) {
        return {
          value: <ExploreDurationMessage asset={explore} daysOnly />,
          className: days < 5 ? styles.red : "",
        };
      }
    }
    if (matchingAssetCredit) {
      if (matchingAssetCredit.term.termType === "numYears") {
        return {
          value: (
            <FormattedMessage
              defaultMessage="{duration, plural, =1 {1 year} other {# years}}"
              values={{ duration: matchingAssetCredit.term.duration }}
            />
          ),
        };
      } else if (matchingAssetCredit.term.termType === "numDays") {
        return {
          value: (
            <FormattedMessage
              defaultMessage="{duration, plural, =1 {1 day} other {# days}}"
              values={{ duration: matchingAssetCredit.term.duration }}
            />
          ),
        };
      } else {
        return {
          value: (
            <FormattedMessage
              defaultMessage="{duration, plural, =1 {1 hour} other {# hours}}"
              values={{ duration: matchingAssetCredit.term.duration }}
            />
          ),
        };
      }
    }
    return { value: "" };
  };

  const needsReturn =
    borrowStatus === BorrowStatus.NoCredits ||
    borrowStatus === BorrowStatus.NoCreditsOrCopies;

  const getReturnMessage = () => {
    if (needsReturn) {
      return (
        <FormattedMessage defaultMessage="Return another item to borrow this one." />
      );
    }

    if (explore.returnable && !explore.active) {
      return <FormattedMessage defaultMessage="Returns Available" />;
    }

    return (
      <div className={styles.return}>
        <FormattedMessage defaultMessage="Item will automatically be returned after loan period ends." />
        {explore.returnable && (
          <ReturnButton
            asset={explore}
            className={clsx(
              "vst-button-pill",
              "vst-button-variant-outline",
              "vst-button-size-small"
            )}
          />
        )}
      </div>
    );
  };

  const shouldShowLoanPeriod = () => {
    const neverExpires = explore.expirationDate !== ("never" as const);
    const expiringSoon =
      explore.expirationDate && explore.expirationDate !== "never"
        ? getDaysRemaining(explore.expirationDate) <= 30
        : false;

    return (
      (explore.active && expiringSoon) ||
      (explore.distributable && !neverExpires)
    );
  };

  const fields: FieldType[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Availability" }),
      // Activating an asset hides this field.
      // 'Copies' omittance on inactive distributable assets means unlimited copies.
      // Nondistributable assets also don't have 'Copies', but this field must be
      // enabled for those assets per specs.
      // This field is also required for informational purposes if a user does not
      // have enough credits.
      enabled:
        !explore.active &&
        (!!matchingAssetCredit?.copies ||
          !explore.distributable ||
          !creditsAvailable),
      ...getAvailability(),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Loan Period" }),
      enabled: shouldShowLoanPeriod(),
      ...getLoanPeriodValue(),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Borrowing Limit" }),
      value: (
        <FormattedMessage
          defaultMessage="Borrowing limit reached. {used} of {total, plural, =1 {1 item} other {# items}}."
          values={{
            used: matchingUserCredit?.credits?.used,
            total: matchingUserCredit?.credits?.total,
          }}
        />
      ),
      enabled:
        (borrowStatus === BorrowStatus.NoCredits ||
          borrowStatus === BorrowStatus.NoCreditsOrCopies) &&
        !explore.active &&
        explore.distributable,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Returns" }),
      value: getReturnMessage(),
      enabled: needsReturn || explore.returnable,
    },
  ].filter((field) => field.enabled);

  if (
    fields.length === 0 ||
    getBorrowStatus() === BorrowStatus.UnlimitedCreditsAndCopies
  ) {
    return null;
  }

  return (
    <div className="vst-definition-list vst-definition-list-size-small">
      <h3>
        <FormattedMessage defaultMessage="Borrowing Details" />
      </h3>
      <dl>
        {fields.map(({ label, value, className }) => (
          <Fragment key={label}>
            <dt>{label}</dt>
            <dd className={className}>{value}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}
